import React, { useContext, useEffect, useMemo, useState } from 'react'
import API from '../../../../../services/API'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { GlobalContext } from '../../../../../App'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PaletteIcon from '@mui/icons-material/Palette'
import SaveIcon from '@mui/icons-material/Save'
import TuneIcon from '@mui/icons-material/Tune'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

const defaultLevels = [
  { name: 'General', color: '#242F40' },
  { name: 'Intermediate', color: '#85276A' },
  { name: 'Advanced', color: '#E32525' },
  { name: 'Specialist', color: '#1F3D7C' },
  { name: 'Other', color: '#239AA4' },
]

const defaultLevelColorMap = Object.fromEntries(
  defaultLevels.map((x) => [x.name.trim().toLowerCase(), x.color]),
)

const normaliseBagLevel = (level) => {
  const normaliseCustomAlarms = (customAlarms) => ({
    enabled: !!customAlarms?.enabled,
    medicines: Array.isArray(customAlarms?.medicines)
      ? customAlarms.medicines
          .map((alarm) => ({
            medicine:
              typeof alarm?.medicine === 'object'
                ? alarm.medicine?._id
                : alarm?.medicine,
            minNumberInBags:
              alarm?.minNumberInBags === '' ||
              alarm?.minNumberInBags === undefined ||
              alarm?.minNumberInBags === null
                ? ''
                : Number(alarm.minNumberInBags),
          }))
          .filter((alarm) => alarm.medicine)
      : [],
  })

  if (typeof level === 'string') {
    const trimmed = level.trim()
    return {
      name: trimmed,
      color: defaultLevelColorMap[trimmed.toLowerCase()] || '#9e9e9e',
      customAlarms: { enabled: false, medicines: [] },
    }
  }

  if (level?.name) {
    const trimmed = level.name.trim()
    return {
      name: trimmed,
      color:
        level.color || defaultLevelColorMap[trimmed.toLowerCase()] || '#9e9e9e',
      customAlarms: normaliseCustomAlarms(level.customAlarms),
    }
  }

  if (typeof level === 'object' && level !== null) {
    const [name, color] = Object.entries(level)[0] || []
    const trimmed = name?.trim?.() || ''
    return {
      name: trimmed,
      color: color || defaultLevelColorMap[trimmed.toLowerCase()] || '#9e9e9e',
      customAlarms: { enabled: false, medicines: [] },
    }
  }

  return {
    name: '',
    color: '#9e9e9e',
    customAlarms: { enabled: false, medicines: [] },
  }
}

const BagLevels = () => {
  const [levels, setLevels] = useState(defaultLevels)
  const [bags, setBags] = useState([])
  const [medicines, setMedicines] = useState([])
  const [alarmEditorIndex, setAlarmEditorIndex] = useState(null)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/settings?select=medsmanagement.bagLevels').then((res) => {
      const data = res?.data?.medsmanagement?.bagLevels

      if (res.status === 200 && Array.isArray(data) && data.length) {
        const normalised = data.map(normaliseBagLevel).filter((l) => l.name)

        setLevels(normalised)
      }
    })
    // Same data MedicineStores.jsx and Medicines.jsx already fetch - reused
    // here purely to compute usage counts and detect drift, nothing more.
    API.get('/siteConfig/medicines/locations').then((res) => {
      if (res.status === 200) setBags(res.data)
    })
    API.get('/siteConfig/medicines').then((res) => {
      if (res.status === 200) setMedicines(res.data)
    })
  }, [])
  const names = levels
    .map((l) => l?.name?.trim()?.toLowerCase())
    .filter(Boolean)

  const hasDuplicates = new Set(names).size !== names.length

  const configuredNames = useMemo(
    () =>
      new Set(
        levels.map((l) => l?.name?.trim()?.toLowerCase()).filter(Boolean),
      ),
    [levels],
  )

  const bagLevelNames = useMemo(
    () =>
      bags
        .filter((b) => b.type === 'Medicine Bag')
        .map((b) => (typeof b.level === 'string' ? b.level.trim() : ''))
        .filter(Boolean),
    [bags],
  )

  const medicineLevelNames = useMemo(
    () =>
      medicines.flatMap((m) =>
        Array.isArray(m.level)
          ? m.level.map((l) => l?.trim()).filter(Boolean)
          : [],
      ),
    [medicines],
  )

  const usageFor = (levelName) => {
    const key = levelName.trim().toLowerCase()
    const bagCount = bagLevelNames.filter((n) => n.toLowerCase() === key).length
    const medicineCount = medicineLevelNames.filter(
      (n) => n.toLowerCase() === key,
    ).length
    return { bagCount, medicineCount }
  }

  const orphanedLevels = useMemo(() => {
    const orphans = new Map()
    ;[...bagLevelNames, ...medicineLevelNames].forEach((name) => {
      const key = name.toLowerCase()
      if (configuredNames.has(key)) return
      const existing = orphans.get(key) || {
        name,
        bagCount: 0,
        medicineCount: 0,
      }
      orphans.set(key, existing)
    })
    bagLevelNames.forEach((name) => {
      const key = name.toLowerCase()
      if (orphans.has(key)) orphans.get(key).bagCount += 1
    })
    medicineLevelNames.forEach((name) => {
      const key = name.toLowerCase()
      if (orphans.has(key)) orphans.get(key).medicineCount += 1
    })
    return [...orphans.values()]
  }, [bagLevelNames, medicineLevelNames, configuredNames])

  const handleRemoveLevel = (index) => {
    const level = levels[index]
    const { bagCount, medicineCount } = usageFor(level?.name || '')

    if (bagCount > 0 || medicineCount > 0) {
      const proceed = window.confirm(
        `"${level.name}" is used by ${bagCount} bag(s) and ${medicineCount} medicine(s). ` +
          `Removing it here won't change those records, but they'll no longer match a configured level. Continue?`,
      )
      if (!proceed) return
    }

    setLevels((prev) => prev.filter((_, i) => i !== index))
  }

  const updateLevel = (index, update) => {
    setLevels((prev) =>
      prev.map((level, i) => (i === index ? { ...level, ...update } : level)),
    )
  }

  const toggleCustomAlarms = (index, checked) => {
    setLevels((prev) =>
      prev.map((level, i) =>
        i === index
          ? {
              ...level,
              customAlarms: {
                enabled: checked,
                medicines: level?.customAlarms?.medicines ?? [],
              },
            }
          : level,
      ),
    )
  }

  const setMedicineAlarm = (levelIndex, medicineId, value) => {
    setLevels((prev) =>
      prev.map((level, i) => {
        if (i !== levelIndex) return level

        const medicines = (level?.customAlarms?.medicines ?? []).filter(
          (alarm) => alarm.medicine !== medicineId,
        )

        if (value !== '') {
          medicines.push({
            medicine: medicineId,
            minNumberInBags: Number(value),
          })
        }

        return {
          ...level,
          customAlarms: {
            enabled: true,
            medicines,
          },
        }
      }),
    )
  }

  const saveLevels = () => {
    const cleanedLevels = levels
      .filter((l) => l?.name?.trim())
      .map((l) => ({
        name: l.name.trim(),
        color: l.color || '#9e9e9e',
        customAlarms: {
          enabled: !!l?.customAlarms?.enabled,
          medicines: (l?.customAlarms?.medicines ?? [])
            .filter(
              (alarm) =>
                alarm?.medicine &&
                alarm?.minNumberInBags !== '' &&
                alarm?.minNumberInBags !== undefined &&
                alarm?.minNumberInBags !== null,
            )
            .map((alarm) => ({
              medicine: alarm.medicine,
              minNumberInBags: Number(alarm.minNumberInBags),
            })),
        },
      }))

    API.put('/site-config', {
      'medsmanagement.bagLevels': cleanedLevels,
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Bag Levels Updated' })
        setLevels(cleanedLevels)
      }
    })
  }

  const alarmEditorLevel =
    alarmEditorIndex === null ? null : levels[alarmEditorIndex]

  const sortedMedicines = useMemo(
    () =>
      [...medicines].sort((a, b) =>
        (a.title ?? '').localeCompare(b.title ?? '', undefined, {
          sensitivity: 'base',
        }),
      ),
    [medicines],
  )

  return (
    <Paper
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 12px 28px rgba(17, 24, 39, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={2}
        sx={{
          p: 2,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" gap={1.25} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'anBlue.main',
              color: 'white',
            }}
          >
            <TuneIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="title3">Medicine Bag Levels</Typography>
            <Typography variant="body2" color="text.secondary">
              Define the capability levels used for bags and formulary access.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={() =>
              setLevels((prev) => [
                ...prev,
                {
                  name: '',
                  color: '#9e9e9e',
                  customAlarms: { enabled: false, medicines: [] },
                },
              ])
            }
          >
            Add Level
          </Button>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            color="anGreen"
            size="small"
            onClick={saveLevels}
            disabled={hasDuplicates}
          >
            {hasDuplicates ? 'Remove Duplicates' : 'Save'}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
          <Chip
            label={`${levels.filter((l) => l?.name?.trim()).length} configured levels`}
            variant="outlined"
          />
          <Chip
            label={`${bagLevelNames.length} bag assignments`}
            variant="outlined"
          />
          <Chip
            label={`${medicineLevelNames.length} formulary assignments`}
            variant="outlined"
          />
        </Stack>

        {orphanedLevels.length > 0 && (
          <Alert severity="warning">
            {orphanedLevels.length === 1
              ? `"${orphanedLevels[0].name}" is`
              : `${orphanedLevels.map((o) => `"${o.name}"`).join(', ')} are`}{' '}
            used by {orphanedLevels.reduce((acc, o) => acc + o.bagCount, 0)}{' '}
            bag(s) and{' '}
            {orphanedLevels.reduce((acc, o) => acc + o.medicineCount, 0)}{' '}
            medicine(s) but {orphanedLevels.length === 1 ? "isn't" : "aren't"}{' '}
            in the list below - add{' '}
            {orphanedLevels.length === 1 ? 'it' : 'them'} back, or update those
            bags/medicines to a current level.
          </Alert>
        )}

        {hasDuplicates && (
          <Alert severity="error">
            Level names must be unique before they can be saved.
          </Alert>
        )}

        <Stack gap={1.25}>
          {levels.map((level, index) => {
            const { bagCount, medicineCount } = usageFor(level?.name || '')
            const overrideCount = level?.customAlarms?.medicines?.length ?? 0
            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  borderLeft: '6px solid',
                  borderLeftColor: level?.color ?? '#9e9e9e',
                }}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  alignItems={{ xs: 'stretch', lg: 'center' }}
                  gap={1.5}
                >
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    minWidth={{ xs: 0, lg: 220 }}
                    flex={1}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: level?.color ?? '#9e9e9e',
                        border: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" noWrap>
                        {level?.name?.trim() || 'Unnamed level'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Used by {bagCount} bag(s), {medicineCount} medicine(s)
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(180px, 1fr) auto',
                      },
                      gap: 1,
                      alignItems: 'center',
                      flex: 1.6,
                    }}
                  >
                    <TextField
                      value={level?.name ?? ''}
                      label="Level Name"
                      size="small"
                      fullWidth
                      onChange={(e) =>
                        updateLevel(index, { name: e.target.value })
                      }
                    />

                    <Stack direction="row" gap={1} alignItems="center">
                      <PaletteIcon fontSize="small" color="action" />
                      <input
                        type="color"
                        value={level?.color ?? '#9e9e9e'}
                        onChange={(e) =>
                          updateLevel(index, { color: e.target.value })
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {level?.color ?? '#9e9e9e'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="flex-end"
                    flex={1}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={!!level?.customAlarms?.enabled}
                          onChange={(e) =>
                            toggleCustomAlarms(index, e.target.checked)
                          }
                        />
                      }
                      label="Custom alarms"
                    />
                    <Button
                      startIcon={<NotificationsActiveIcon />}
                      size="small"
                      variant="outlined"
                      disabled={!level?.customAlarms?.enabled}
                      onClick={() => setAlarmEditorIndex(index)}
                    >
                      {overrideCount > 0
                        ? `${overrideCount} override${overrideCount === 1 ? '' : 's'}`
                        : 'Manage'}
                    </Button>
                    <Tooltip title="Remove level">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLevel(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      </Stack>

      <Dialog
        open={alarmEditorIndex !== null}
        onClose={() => setAlarmEditorIndex(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle color="anBlue.main">
          {alarmEditorLevel?.name || 'Bag level'} custom alarms
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={1.25}>
            <Alert severity="info">
              Leave a medicine blank to use its standard formulary bag alarm.
            </Alert>
            {sortedMedicines.map((medicine) => {
              const alarm = alarmEditorLevel?.customAlarms?.medicines?.find(
                (item) => item.medicine === medicine._id,
              )

              return (
                <Paper
                  key={medicine._id}
                  variant="outlined"
                  sx={{ p: 1.25, borderRadius: 1 }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={1.25}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" noWrap>
                        {medicine.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Standard alarm: {medicine.minNumberInBags ?? 0}
                      </Typography>
                    </Box>
                    <TextField
                      type="number"
                      size="small"
                      label="Custom bag alarm"
                      autoComplete="off"
                      value={alarm?.minNumberInBags ?? ''}
                      inputProps={{ min: 0 }}
                      sx={{ width: { xs: '100%', md: 180 } }}
                      onChange={(e) =>
                        setMedicineAlarm(
                          alarmEditorIndex,
                          medicine._id,
                          e.target.value,
                        )
                      }
                    />
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlarmEditorIndex(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default BagLevels
